import {
  AttachmentRequestRecord,
  ClusterDetachRecord,
  ConnectionRecord,
  ContentNotificationRecord,
  NodeStateRecord,
  PeerNotificationRecord,
  PeerRequestRecord,
  TransferRecord
} from '../../../webrtc/agent/interfaces';
import DataReportBlock, {
  mergeReportBlock
} from '../../../reporter/report/DataReportBlock';
import DataReportMap, {
  getDataReportMap
} from '../../../reporter/report/DataReportMap';
import Locality from '../../../locality/Locality';
import LogReport from './LogReport';
import OverflowData from './OverflowData';
import MaximumReportConfig from '../../../config/MaximumReportConfig';
import logger from '../../../log';
import {
  Ecdn10Block,
  Ecdn11Block,
  GeneralBlock,
  Report
} from '../../../reporter/report/Report';
import { LogReporterMessage } from './interfaces';
import { ReporterMessage } from '../../../log';
import ToggleConfig from '../../../config/ToggleConfig';
import filterTransfers from './filterTransfers';
import filterConnections from './filterConnections';
import filterEcdn10 from './filterEcdn10';
import filterLocality from './filterLocality';
import filterEcdn11 from './filterEcdn11';
import filterGeneral from './filterGeneral';
import mergeObjects from '../../../util/mergeObjects';

const log = logger.getLogger(__filename);

export default class PendingReport {
  config: MaximumReportConfig;
  // A token is added in the formatReportRequest
  general: GeneralBlock;
  bitrates: DataReportBlock<unknown>;
  detection: DataReportBlock<unknown>;
  events: DataReportBlock<unknown>;
  log: DataReportMap<ReporterMessage, LogReport, LogReporterMessage>;
  overflows: OverflowData;
  priming: DataReportBlock<unknown>;
  ecdn10?: Ecdn10Block;
  ecdn11?: Ecdn11Block;
  localityData?: Locality;
  transfers: DataReportBlock<TransferRecord>;
  connections: DataReportBlock<ConnectionRecord>;
  peerNotifications: DataReportBlock<PeerNotificationRecord>;
  contentNotifications: DataReportBlock<ContentNotificationRecord>;
  detachments: DataReportBlock<ClusterDetachRecord>;
  nodeState: DataReportBlock<NodeStateRecord>;
  peerRequests: DataReportBlock<PeerRequestRecord>;
  attachmentRequests: DataReportBlock<AttachmentRequestRecord>;

  constructor(item?: PendingReport) {
    const i = item || ({} as PendingReport);
    const config = i.config || new MaximumReportConfig();
    this.overflows = new OverflowData();
    this.config = config;
    this.attachmentRequests = i.attachmentRequests || new DataReportBlock();
    this.bitrates = i.bitrates || new DataReportBlock();
    this.connections = i.connections || new DataReportBlock();
    this.contentNotifications = i.contentNotifications || new DataReportBlock();
    this.detachments = i.detachments || new DataReportBlock();
    this.detection = i.detection || new DataReportBlock();
    this.ecdn10 = new Ecdn10Block(i.ecdn10);
    this.ecdn11 = new Ecdn11Block(i.ecdn11);
    this.events = i.events || new DataReportBlock();
    this.general = new GeneralBlock(i.general);
    this.localityData = i.localityData;
    this.nodeState = i.nodeState || new DataReportBlock();
    this.peerNotifications = i.peerNotifications || new DataReportBlock();
    this.peerRequests = i.peerRequests || new DataReportBlock();
    this.priming = i.priming || new DataReportBlock();
    this.transfers = i.transfers || new DataReportBlock();

    this.log =
      i.log instanceof DataReportMap
        ? i.log
        : (this.log = getDataReportMap({
            max: config.log,
            name: 'log',
            overflowReporter: this.overflows.add,
            toStorage: LogReport.create
          }));

    // SRV-1703: Log message for failure to copy log into this structure.
    if (i.log && !(i.log instanceof DataReportMap)) {
      const msg = Object.entries(i.log).reduce((acc, [key, value]) => {
        acc[key] = typeof value;
        return acc;
      }, {} as Record<string, string>);
      log.warn('Unable to add existing logs, they are not of right type', msg);
    }
  }

  mergeData(reports?: Report[]): void {
    if (!reports || reports.length == 0) {
      return;
    }
    reports = reports.filter(r => !!r);
    const dest = this as unknown as Record<string, unknown>;
    reports.forEach(report => {
      Object.entries(report).forEach(entry => mergeReportBlock(dest, ...entry));
    });
    const a = [...reports, this as unknown as Report];
    this.ecdn10 = new Ecdn10Block(
      mergeObjects(
        a.map(r => r.ecdn10),
        true
      )
    );
    this.ecdn11 = new Ecdn11Block(
      mergeObjects(
        a.map(r => r.ecdn11),
        true
      )
    );
    this.general = new GeneralBlock(
      mergeObjects(
        a.map(r => r.general),
        true
      )
    );
    const data = new Locality(
      mergeObjects(
        a.map(r => r.localityData),
        true
      )
    );
    this.localityData = data;
  }

  toReport(toggle: ToggleConfig, report?: Report[]): Report {
    this.mergeData(report);
    // NOTE: If we are already at the maximum logs, this could increase the log
    // count by one.
    this.overflows.report(this.log);
    return {
      attachmentRequests: this.attachmentRequests,
      bitrates: this.bitrates,
      connections: filterConnections(this.connections, toggle),
      contentNotifications: this.contentNotifications,
      detachments: this.detachments,
      detection: this.detection,
      ecdn10: filterEcdn10(this.ecdn10, toggle),
      ecdn11: filterEcdn11(this.ecdn11, toggle),
      events: this.events,
      general: filterGeneral(this.general, toggle),
      localityData: filterLocality(this.localityData, toggle),
      log: this.config.log > 0 ? this.log.toReport() : undefined,
      nodeState: this.nodeState,
      peerNotifications: this.peerNotifications,
      peerRequests: this.peerRequests,
      priming: this.priming,
      transfers: filterTransfers(this.transfers, toggle)
    };
  }
}
